import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useReducer,
} from 'react';
import * as SurveyReact from 'survey-react';
import { UNSAFE_NavigationContext as UnsafeNavigationContext } from 'react-router-dom';

import { debounce, findIndex } from 'lodash';

import api from 'api';
import useUser from 'hooks/useUser';
import useCurrentApplication from 'hooks/useCurrentApplication';
import useChat from 'hooks/useChat';
import useFeedback from 'hooks/useFeedback';
import { surveyJSTheme } from 'helpers/surveyJs';
import { formatDate } from 'utils/dateTime';

import Loader from 'components/elements/Loader';
import Modal from 'components/elements/BasicModal';
import SurveyProgress from 'components/forms/helpers/SurveyProgress';
import SurveyComments from 'components/forms/helpers/SurveyComments';
import SurveyPageOverrider from 'components/forms/helpers/overrideSurveyJS/SurveyPageOverrider';
import SurveyQuestionOverrider from 'components/forms/helpers/overrideSurveyJS/SurveyQuestionOverrider';
import surveyFileQuestionOverride from 'components/forms/helpers/overrideSurveyJS/surveyFileQuestionOverride';
import SurveyNavigation from 'components/forms/helpers/SurveyNavigation';
import FooterNavigation from 'components/forms/helpers/SurveyNavigation/FooterNavigation';
import SiteReviewerComments from 'components/forms/helpers/SiteReviewerComments';

import {
  RetryButton,
  SaveStatusBar,
  SaveStatusWrapper,
  SurveyPanel,
  SurveyContainer,
  SubmitInfo,
} from './index.styles';

SurveyReact.settings.panelMaximumPanelCount = 4500;
SurveyReact.StylesManager.applyTheme(surveyJSTheme);

interface Page {
  name: string;
  [key: string]: any; // for any additional page-specific properties
}

interface PartialResultFormProps {
  canEdit: boolean;
  onSubmit: () => void;
  surveyVersionId: number;
  formType: string;
  uploadFileFunc?: (args: { appId: number; file: File }) => Promise<{ data: { id: string } }>;
  showReviwerComments?: boolean;
  readOnlyReviwerComments?: boolean;
  submittedAt?: string;
  enableSaveStatusBar?: boolean;
}

type SurveyModel = SurveyReact.Model | null;
type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'failed';
const NavigationContext = UnsafeNavigationContext;

const convertDotNetTicksToDate = (ticks: number): Date => {
  const epochTicks = 621355968000000000; // Ticks from 01/01/0001 to 01/01/1970
  const ticksPerMillisecond = 10000; // .NET ticks are in 100-nanosecond intervals
  return new Date((ticks - epochTicks) / ticksPerMillisecond);
};

const getNormalizedDateString = (value: unknown) => {
  if (!value) return null;

  // Try to parse as .NET ticks (numeric string or number)
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof numericValue === 'number' && !Number.isNaN(numericValue) && numericValue > 621355968000000000) {
    // Likely a .NET tick value (very large number)
    try {
      const date = convertDotNetTicksToDate(numericValue);
      return Number.isNaN(date.getTime()) ? null : date.toISOString();
    } catch {
      return null;
    }
  }

  // Fallback to standard date parsing
  const date = new Date(value as string | number | Date);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const getLatestSavedAt = (data: Record<string, unknown> = {}) => Object.entries(data)
  .filter(([key]) => /^savedAt\d+$/.test(key))
  .map(([, value]) => getNormalizedDateString(value))
  .filter((value): value is string => !!value)
  .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;

const getLastSavedLabel = (value: string | null) => {
  if (!value) return 'Not saved yet';
  const formattedValue = formatDate(value, 'YYYY-MM-DD HH:mm:ss');
  return formattedValue === 'Invalid date' ? 'Not saved yet' : formattedValue;
};

const PartialResultForm: React.FC<PartialResultFormProps> = ({
  canEdit,
  onSubmit,
  surveyVersionId,
  formType,
  uploadFileFunc,
  showReviwerComments = false,
  readOnlyReviwerComments = false,
  submittedAt,
  enableSaveStatusBar = false,
}) => {
  const feedback = useFeedback();
  const { appData, userTypeId } = useCurrentApplication();
  const { isAdmin } = useUser();
  const [model, setModel] = useState<SurveyModel>(null);
  const [changed, setChanged] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [overrideModal, setOverrideModal] = useState<{ cb?:() => void } | null>(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{ retry:() => void } | null>(null);
  const saveInFlightRef = useRef(false);
  const pendingSaveRef = useRef<{ overwrite?: boolean; cb?:() => void } | null>(null);
  const changeVersionRef = useRef(0);
  const programmaticUpdateRef = useRef(false);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const navigationContext = useContext(NavigationContext);

  const { closeQuestionComments } = useChat();

  const isEditable = useMemo(
    () => (isAdmin || canEdit) && !appData?.archivedAt,
    [isAdmin, canEdit, appData?.archivedAt],
  );
  const shouldWarnOnLeave = useMemo(
    () => enableSaveStatusBar && isEditable && (changed || saveStatus === 'saving'),
    [changed, enableSaveStatusBar, isEditable, saveStatus],
  );

  const saveStatusLabel = useMemo(() => {
    if (appData?.archivedAt && saveStatus === 'saved') return 'Read-only';
    switch (saveStatus) {
      case 'saving':
        return 'Saving changes...';
      case 'unsaved':
        return 'Unsaved changes';
      case 'failed':
        return 'Save failed';
      case 'saved':
      default:
        return 'All changes saved';
    }
  }, [saveStatus, appData?.archivedAt]);

  useEffect(() => {
    api.getSurveyVersion(surveyVersionId).then(({ data }) => {
      api
        .getFormResults({
          appId: appData.id,
          formType,
          pageIndex: null,
        })
        .then(({ data: { data: formData } }) => {
          const newModel = new SurveyReact.Model(data.model);
          newModel.data = formData;
          newModel.mode = isEditable ? 'edit' : 'display';
          setModel(newModel);
          setLastSavedAt(getLatestSavedAt(formData));
          setSaveStatus('saved');
        });
    });
  }, [appData, isEditable, surveyVersionId, formType]);

  useEffect(() => {
    if (!shouldWarnOnLeave) return undefined;

    const navigator = navigationContext?.navigator as any;
    if (!navigator) return undefined;

    const createRetry = (retry: () => void) => {
      setPendingNavigation((current) => {
        if (current) return current;
        return { retry };
      });
      setShowLeaveModal(true);
    };

    if (!navigator.block) {
      const originalPush = navigator.push?.bind(navigator);
      const originalReplace = navigator.replace?.bind(navigator);
      const originalGo = navigator.go?.bind(navigator);

      if (originalPush) {
        navigator.push = (...args: any[]) => {
          createRetry(() => originalPush(...args));
        };
      }

      if (originalReplace) {
        navigator.replace = (...args: any[]) => {
          createRetry(() => originalReplace(...args));
        };
      }

      if (originalGo) {
        navigator.go = (...args: any[]) => {
          createRetry(() => originalGo(...args));
        };
      }

      return () => {
        if (originalPush) navigator.push = originalPush;
        if (originalReplace) navigator.replace = originalReplace;
        if (originalGo) navigator.go = originalGo;
      };
    }

    const unblock = navigator.block((tx: { retry: () => void }) => {
      createRetry(() => {
        unblock();
        tx.retry();
      });
    });

    return unblock;
  }, [navigationContext, shouldWarnOnLeave]);

  useEffect(() => {
    if (!shouldWarnOnLeave) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const unloadEvent = event;
      unloadEvent.preventDefault();
      Reflect.set(unloadEvent, 'returnValue', '');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldWarnOnLeave]);

  const handleSave = useCallback(
    ({ overwrite, cb }: { overwrite?: boolean; cb?: () => void }) => {
      // Do not save if verification is archived.
      if (appData?.archivedAt) return;
      if (!model) return;

      if (saveInFlightRef.current) {
        pendingSaveRef.current = { overwrite, cb };
        return;
      }

      const { currentPage, pages, data } = model;
      const pageIndex = findIndex(pages, { id: currentPage.id });
      const savedAtId = `savedAt${pageIndex}`;
      const saveAtValue = data[savedAtId];
      const saveVersion = changeVersionRef.current;

      saveInFlightRef.current = true;

      if (enableSaveStatusBar) {
        setSaveStatus('saving');
      }

      api
        .saveFormResults({
          appId: appData.id,
          formType,
          pageIndex,
          data: {
            ...currentPage.getValue(),
            [savedAtId]: saveAtValue,
          },
          overwrite: overwrite || !saveAtValue,
        })
        .then(({ data: newSavedAt }) => {
          const updatedSavedAt = newSavedAt || saveAtValue;
          const currentData = model.data;
          model.data = { ...currentData, [savedAtId]: updatedSavedAt };
          if (!enableSaveStatusBar) {
            feedback.setSuccess('Saved successfully');
          }
          const normalizedSavedAt = getNormalizedDateString(updatedSavedAt);
          setLastSavedAt(normalizedSavedAt || new Date().toISOString());
          const noNewChanges = changeVersionRef.current === saveVersion;
          setChanged(!noNewChanges);
          setSaveStatus(noNewChanges ? 'saved' : 'unsaved');
          forceUpdate();
          if (cb && noNewChanges && !pendingSaveRef.current) {
            cb();
          }
          if (cb && (!noNewChanges || !!pendingSaveRef.current)) {
            pendingSaveRef.current = {
              ...(pendingSaveRef.current || {}),
              cb,
            };
          }
        })
        .catch(({ response }) => {
          if (response?.data?.detail === 'Results are changed since last loading, please refresh page') {
            setSaveStatus('failed');
            setOverrideModal({ cb });
            return;
          }
          feedback.setError('Error saving form');
          setSaveStatus('failed');
        })
        .finally(() => {
          saveInFlightRef.current = false;

          if (pendingSaveRef.current) {
            const nextSave = pendingSaveRef.current;
            pendingSaveRef.current = null;
            handleSave(nextSave);
          }
        });
    },
    [appData, enableSaveStatusBar, feedback, formType, model],
  );

  const getPartialPageResults = useCallback(
    (page: Page) => {
      if (!model) return;
      const pageIndex = findIndex(model.pages, { id: page.id });
      api
        .getFormResults({
          appId: appData.id,
          formType,
          pageIndex,
        })
        .then(({ data: { data } }) => {
          const currentData = model.data;
          programmaticUpdateRef.current = true;
          model.data = { ...currentData, ...data };
          forceUpdate();
          setTimeout(() => { programmaticUpdateRef.current = false; }, 0);
        });
    },
    [appData, formType, model],
  );

  const handleSaveRef = useRef(handleSave);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  const onSave = useMemo(
    () => debounce(() => handleSaveRef.current({}), 300),
    [],
  );

  useEffect(() => () => { onSave.cancel(); }, [onSave]);

  const onSurveyValueChanged = useCallback((_sender: any, _options: any) => {
    if (programmaticUpdateRef.current) return;
    if (appData?.archivedAt) return;
    onSave();
  }, [onSave, appData?.archivedAt]);

  const onPageChanged = useCallback(
    (newPage: Page) => {
      if (!model) return;
      if (!isEditable || !changed) {
        model.currentPage = newPage;
        forceUpdate();
        return;
      }
      if (appData?.archivedAt) {
        model.currentPage = newPage;
        forceUpdate();
        return;
      }

      onSave.cancel();
      handleSave({
        cb: () => {
          model.currentPage = newPage;
          forceUpdate();
        },
      });
    },
    [changed, handleSave, isEditable, model, onSave, appData?.archivedAt],
  );

  const handleFileUpload = (opt: {
    files: File[];
    callback: (status: string, items: any[]) => void;
  }) => {
    opt.files.forEach((file) => {
      if (!uploadFileFunc) return;
      uploadFileFunc({
        appId: appData.id,
        file,
      }).then(({ data }) => opt.callback('success', [
        {
          file,
          content: `${data.id}`,
        },
      ]));
    });
  };

  if (!model) return <Loader />;

  return (
    <div>
      <Modal
        title="Unsaved changes"
        body="You have unsaved changes or a save is in progress. Are you sure you want to leave this page?"
        isOpen={showLeaveModal}
        onClose={() => {
          setPendingNavigation(null);
          setShowLeaveModal(false);
        }}
        onConfirm={() => {
          pendingNavigation?.retry();
          setPendingNavigation(null);
          setShowLeaveModal(false);
        }}
      />
      <Modal
        title="A change to has been made to this page/question"
        body="Would you like to overwrite? This will discard their changes."
        isOpen={!!overrideModal}
        onClose={() => {
          if (model) getPartialPageResults(model.currentPage);
          setOverrideModal(null);
        }}
        onConfirm={() => {
          handleSave({
            overwrite: true,
            cb: () => {
              if (overrideModal?.cb) overrideModal.cb();
              setOverrideModal(null);
            },
          });
        }}
      />
      {enableSaveStatusBar && (
        <SaveStatusWrapper>
          <SaveStatusBar status={saveStatus}>
            <div className="status-label">{saveStatusLabel}</div>
            <div className="timestamp-label">
              {`Last saved: ${getLastSavedLabel(lastSavedAt)}`}
            </div>
            {saveStatus === 'failed' && (
              <RetryButton
                type="button"
                onClick={() => handleSave({})}
              >
                Retry
              </RetryButton>
            )}
          </SaveStatusBar>
        </SaveStatusWrapper>
      )}
      <SurveyProgress pages={model.pages} />
      <SurveyPanel>
        <SurveyNavigation
          model={model}
          onPageChanged={onPageChanged}
          onSubmit={onSubmit}
          surveyVersionId={surveyVersionId}
          formType={formType}
        />
        <SurveyContainer _hasbuttons={!isAdmin}>
          {showReviwerComments && (
            <SiteReviewerComments
              applicationId={appData.id}
              surveyVersionId={surveyVersionId}
              criteriaId={model.currentPage.name}
              readOnly={isAdmin || readOnlyReviwerComments}
            />
          )}
          <SubmitInfo>{`Submitted on: ${formatDate(submittedAt)}`}</SubmitInfo>
          <SurveyReact.Survey
            model={model}
            onValueChanged={onSurveyValueChanged}
            onValueChanging={(_sender: any, _options: any) => {
              if (programmaticUpdateRef.current) return;
              if (appData?.archivedAt) return;
              changeVersionRef.current += 1;
              setChanged(true);
              if (enableSaveStatusBar && saveStatus !== 'saving') {
                setSaveStatus('unsaved');
              }
            }}
            onCurrentPageChanged={({ currentPage }: { currentPage: SurveyReact.PageModel }) => {
              closeQuestionComments();
              getPartialPageResults(currentPage);
            }}
            showNavigationButtons="none"
            onAfterRenderPage={(
              _survey: SurveyReact.SurveyModel,
              opt: any,
            ) => SurveyPageOverrider(opt)}
            onAfterRenderQuestion={(m: SurveyReact.SurveyModel, opt: any) => {
              surveyFileQuestionOverride(m, opt);
              return (
                !isAdmin
                && SurveyQuestionOverrider({
                  options: opt,
                  surveyVersionId,
                  userTypeId,
                })
              );
            }}
            onUploadFiles={(_: any, opt: any) => handleFileUpload(opt)}
          />
          <FooterNavigation model={model} onPageChanged={onPageChanged} />
        </SurveyContainer>
        <SurveyComments surveyVersionId={surveyVersionId} />
      </SurveyPanel>
    </div>
  );
};

export default PartialResultForm;
